import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { Box } from 'components/Box';
import { Button } from 'components/Button/Button';
import { Icon } from 'components/Icon';
import { Notifications } from 'components/Notifications';
import { H4 } from 'components/Text/H4';
import {
  fetchNotifications,
  markNotificationsAsViewed,
} from 'modules/main/store/ducks/userNotifications';
import { useMainDispatch } from 'modules/main/store/hooks/useMainDispatch';
import { useMainSelector } from 'modules/main/store/hooks/useMainSelector';
import { useUserEnvironment } from 'modules/main/store/hooks/useUserEnvironment';
import { colors, gutterWidth, transition } from 'theme/theme';
import { MappedNotification } from 'types/mappedNotifications/MappedNotification';
import { trackEvent } from 'utils/analytics/track/trackEvent';
import { isBrowser } from 'utils/browser';
import { getMappedNotificationIds } from 'utils/userNotifications';

export const MobileNotificationsMenuContainer = styled.div<{
  $open: boolean;
}>`
  position: fixed;
  top: 0;
  left: 0;

  width: 100%;
  height: 100vh;
  transition: all ${transition};
  overflow-y: scroll;
  will-change: transform;
  background-color: ${colors.white};
  z-index: 104;

  transform: ${({ $open }) => ($open ? 'translateY(0)' : 'translateY(-100%)')};
`;

export const MobileNotificationsMenuHeader = styled.div`
  align-items: center;
  background: ${colors.white};
  display: flex;
  justify-content: space-between;
  position: relative;
  padding: 0 ${gutterWidth / 2}px;
  height: 60px;
  width: 100%;
  border-bottom: 1px solid ${colors.selectionGrey};
`;

const Tag = styled.div`
  position: absolute;
  right: 18px;
  top: 14px;
  padding: 4px;
  border-radius: 50%;
  background-color: ${colors.decorativePoppy};
`;

const NotificationButton = styled.button`
  padding: 8px 16px;
  &:hover {
    color: ${colors.hoverBlue};
  }
`;

export function MobileNotificationsMenu() {
  const { user } = useUserEnvironment();

  const dispatch = useMainDispatch();

  const { notifications, isLoaded } = useMainSelector(
    (state) => state.userNotifications,
  );

  const [menuOpen, setMenuOpen] = useState(false);

  const unseenNotifications = useMemo(
    () => notifications.filter((notification) => !notification.viewed),
    [notifications],
  );

  const showBadge = useMemo(
    () => isBrowser() && unseenNotifications.length > 0,
    [unseenNotifications],
  );

  const handleView = useCallback(
    (viewedNotifications: MappedNotification[]) =>
      dispatch(
        markNotificationsAsViewed(
          viewedNotifications.flatMap((notification) =>
            getMappedNotificationIds(notification),
          ),
        ),
      ),
    [dispatch],
  );

  const handleClick = useCallback(() => {
    setMenuOpen((open) => !open);

    if (!menuOpen && notifications.length) {
      handleView(notifications);

      trackEvent('Displayed User Menu Notifications', {
        notification_types: notifications.reduce(
          (acc, notification) =>
            // @ts-expect-error TS(2769): No overload matches this call.
            acc.includes(notification.type) ? acc : [...acc, notification.type],
          [],
        ),
      });
    }
  }, [handleView, notifications, menuOpen]);

  useEffect(() => {
    if (user?.hasNotifications && !isLoaded) {
      dispatch(fetchNotifications());
    }
  }, [user?.hasNotifications, dispatch, isLoaded]);

  return (
    <>
      <Box position="relative">
        <NotificationButton
          aria-label={getText('Toggle notifications')}
          data-qa-id="notifications-menu-button"
          onClick={handleClick}
        >
          {showBadge ? <Tag /> : null}
          <Icon name="bell" size={22} />
        </NotificationButton>
      </Box>

      <MobileNotificationsMenuContainer
        $open={menuOpen}
        data-qa-id="mobile-notifications-menu"
      >
        <MobileNotificationsMenuHeader>
          <H4 mb={0} color={colors.brandBlue}>
            {getText('Notifications')}
          </H4>
          <Button
            data-qa-id="close-mobile-notifications"
            onClick={() => setMenuOpen(false)}
            aria-label={getText('Close notifications')}
            variant="tertiary"
            noPadding
            iconName="close"
          />
        </MobileNotificationsMenuHeader>

        <Notifications
          notifications={notifications}
          dismisseable={false}
          onClick={() => setMenuOpen(false)}
        />
      </MobileNotificationsMenuContainer>
    </>
  );
}
